import {
  BadgeCheckIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ShieldCheckIcon,
  XIcon,
} from '@heroicons/react/outline';
import React, { useState } from 'react';

const permissions = [
  {
    category: 'Home',
    permissions: [],
  },
  {
    category: 'Orders',
    permissions: [
      'Manage orders information',
      'Edit orders',
      'Apply discounts',
      'Set payment terms',
      'Charge vaulted card',
      'Charge credit card',
      'Export orders',
      'Mark order as paid',
      'Capture payments',
      'Fulfill and ship',
      'Buy shipping labels',
      'Return orders',
      'Refund orders',
      'Cancel orders',
      'Delete orders',
      'Manage abandoned checkouts',
    ],
  },
  {
    category: 'Draft orders',
    permissions: [
      'Create and edit draft orders',
      'Apply discounts',
      'Set payment terms',
      'Charge credit card',
      'Charge vaulted card',
      'Mark draft orders as paid',
      'Export draft orders',
      'Delete draft orders',
    ],
  },
  {
    category: 'Products',
    permissions: [
      'View product cost',
      'Create and edit products',
      'Edit product cost',
      'Edit product price',
      'Manage inventory',
      'Export products',
      'Delete products',
    ],
  },
  {
    category: 'Gift cards',
    permissions: ['Create and edit', 'Deactivate', 'Export gift cards'],
  },
  {
    category: 'Customers',
    permissions: [
      'Erase customer personal data',
      'Request customer data',
      'Export customers',
      'Merge customers',
    ],
  },
  {
    category: 'Discounts',
    permissions: [],
  },
  {
    category: 'Manage and install apps and channels',
    permissions: ['Approve app charges'],
  },
  {
    category: 'Online store',
    permissions: ['Themes', 'Edit theme code'],
  },
  {
    category: 'Administration',
    permissions: [
      'Manage settings',
      'Checkout',
      'View apps developed by staff and collaborators',
      'Develop apps',
      'Enable app development',
    ],
  },
];

const PermissionsModal = ({ onCloseModal }: { onCloseModal: () => void }) => (
  <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
    <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
      <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Required Permissions{' '}
            <a
              className="ml-2 text-sm mb-[-2px] text-blue-600"
              href="https://help.shipaid.com/en/article/shopify-required-permissions-1eazebf/?bust=1750737981798"
              target="_new"
            >
              Why do we need this?
            </a>
          </h2>
          <p className="text-gray-600 mt-1">
            We need these permissions to provide our services
          </p>
        </div>
        <button
          onClick={onCloseModal}
          className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
        >
          <XIcon className="w-8 h-8" />
        </button>
      </div>

      <div className="p-6 overflow-y-auto max-h-[60vh]">
        <div className="md:columns-2 gap-6">
          {permissions.map((category, index) => (
            <div
              key={index}
              className="mb-6 break-inside-avoid border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-center mb-4">
                <BadgeCheckIcon className="w-8 h-8 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-blue-600 mb-[-2px]">
                  {category.category}
                </h3>
              </div>
              <ul className="space-y-2">
                {category.permissions.map((permission, permIndex) => (
                  <li
                    key={permIndex}
                    className="flex items-center text-sm text-gray-600"
                  >
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3"></div>
                    {permission}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-blue-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-[#2563eb] mb-3">
            Why do we need these permissions?
          </h3>
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              • <strong>Store Management:</strong> To integrate seamlessly with
              your existing setup
            </p>
            <p>
              • <strong>Order Processing:</strong> To optimize your checkout and
              increase conversions
            </p>
            <p>
              • <strong>Customer Data:</strong> To provide personalized shopping
              experiences
            </p>
            <p>
              • <strong>Theme & Content:</strong> To implement our cart widgets
              and features
            </p>
            <p>
              • <strong>Analytics:</strong> To track performance and provide
              insights
            </p>
          </div>
        </div>

        <div className="mt-6 bg-green-50 p-4 rounded-lg">
          <div className="flex items-center text-green-800">
            <ShieldCheckIcon className="mr-2 w-8 h-8" />
            <span className="font-medium">
              Your data is secure and encrypted. We never store sensitive
              information.
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const PermissionsView = () => {
  const shownPermissions = permissions.slice(0, 4);
  const remaining = permissions.length - shownPermissions.length;

  const [showPermissions, setShowPermissions] = useState(false);

  return (
    <>
      {/* Permissions Summary Box */}
      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex -space-x-2 mr-4">
              {shownPermissions.map((_, index) => (
                <div
                  key={index}
                  className="w-10 h-10 rounded-full flex items-center justify-center border-white border-2 bg-green-100"
                >
                  <CheckCircleIcon className="w-6 h-6 text-green-600" />
                </div>
              ))}
              {remaining > 0 && (
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center border-2 border-white text-xs font-semibold text-gray-600">
                  +{remaining}
                </div>
              )}
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">
                {permissions.length} Permission Categories{' '}
                <a
                  className="ml-2 text-sm text-blue-600 underline"
                  href="https://help.shipaid.com/en/article/shopify-required-permissions-1eazebf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Why do we need this?
                </a>
              </h4>
              <p className="text-sm text-gray-600">
                Store management, orders, customers & more
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowPermissions(true)}
            className="flex items-center px-4 py-2 bg-[#2563eb] text-white rounded-lg hover:bg-blue-700 transition-all duration-200 hover:shadow-md"
          >
            View Details
            <ChevronRightIcon className="ml-1 w-4 h-4" />
          </button>
        </div>
      </div>
      {showPermissions && (
        <PermissionsModal onCloseModal={() => setShowPermissions(false)} />
      )}
    </>
  );
};

export default PermissionsView;
